import React from "react"
import "./stories.css"
import { Card } from "@mui/material"
import { lxoSpace, lxoRadius, lxoSurface } from "shared/styles/lxoDashboardTokens"
import ModalStoryShare from "components/ModalStoryShare/ModalStoryShare"
import AvaImg from "assets/images/ava.png"
import { useSelector } from "react-redux"
import { selectAuth } from "shared/redux/slices/authSlice"
import { useTreasureHunt } from "components/useTreasureHunt/useTreasureHunt"
import {
  useLazyGetStoryTeamLXOQuery,
  useLazyGetMyStoryStatusQuery,
  useAddStoryStatusMutation,
} from "shared/redux/endpoints/lxo"

const MAX_VISIBLE_AVATARS = 10

export default function StoryLXO({ activeTeam }) {
  const auth = useSelector(selectAuth)
  const currentUserId = auth?.user?.userId || auth?.user?.id
  const [openModalStoryShare, setOpenModalStoryShare] = React.useState(false)
  const [storyItems, setStoryItems] = React.useState([])
  const [expanded, setExpanded] = React.useState(false)
  const { triggerIfNew } = useTreasureHunt("storylxo-open-modal")

  const [triggerGetStoryTeam] = useLazyGetStoryTeamLXOQuery()
  const [triggerGetMyStoryStatus] = useLazyGetMyStoryStatusQuery()
  const [addStoryStatus] = useAddStoryStatusMutation()
  const handlerClickStory = (index) => {
    if (index === 0) {
      triggerIfNew()
      setOpenModalStoryShare(true)
    }
  }

  const propsModalStoryShare = {
    isOpen: openModalStoryShare,
    onClose: () => setOpenModalStoryShare(false),
    onSubmit: (payload) => {
      const nextMood = payload?.moodEmoji ?? payload?.reactionEmoji ?? null
      const nextStatus = payload?.reactionLabel ?? payload?.reaction ?? null

      if (nextMood || nextStatus) {
        addStoryStatus({
          mood: nextMood ?? "",
          status: nextStatus ?? "",
        })
      }

      setStoryItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== 0) return item
          return {
            ...item,
            status: nextMood ?? item.status,
            note: nextStatus ?? item.note,
          }
        })
      )
    },
  }

  React.useEffect(() => {
    if (!activeTeam) return
    let cancelled = false

    const run = async () => {
      const [teamRes, myRes] = await Promise.all([
        triggerGetStoryTeam(activeTeam),
        triggerGetMyStoryStatus(),
      ])

      if (cancelled) return

      const raw =
        teamRes?.data?.data?.data || teamRes?.data?.data || teamRes?.data || []
      const list = Array.isArray(raw) ? raw : []

      const mapped = list.map((member, idx) => ({
        id: member.userId || member.id || idx + 1,
        name:
          member.fullName ||
          member.name ||
          member.employeeFullName ||
          member.username ||
          "Unknown",
        img: member.avatar || member.img || member.employeeAvatar || AvaImg,
        status:
          member.moodEmoji ||
          member.mood ||
          member.statusEmoji ||
          member.emoji ||
          null,
        note:
          member.status ||
          member.statusText ||
          member.note ||
          member.storyStatus ||
          null,
      }))

      const currentUserItem = mapped.find(
        (item) =>
          item.id && currentUserId && String(item.id) === String(currentUserId)
      )

      const myStatus = myRes?.data?.data || myRes?.data || null

      const youItem = {
        id: currentUserId || "me",
        name: "You",
        img: auth?.user?.avatar || myStatus?.avatar || AvaImg,
        status: myStatus?.mood || currentUserItem?.status || null,
        note:
          myStatus?.statusText || currentUserItem?.note || "Add a story",
      }

      const others = mapped.filter(
        (item) => !currentUserId || String(item.id) !== String(currentUserId)
      )

      setStoryItems([youItem, ...others])
    }

    run()

    return () => {
      cancelled = true
    }
  }, [
    activeTeam,
    currentUserId,
    auth?.user?.avatar,
    triggerGetStoryTeam,
    triggerGetMyStoryStatus,
  ])

  const visibleStories = expanded
    ? storyItems
    : storyItems.slice(0, MAX_VISIBLE_AVATARS)
  const overflowCount = expanded
    ? 0
    : Math.max(0, storyItems.length - MAX_VISIBLE_AVATARS)

  return (
    <Card
      sx={{
        background: lxoSurface.background,
        border: lxoSurface.border,
        borderRadius: `${lxoRadius.surface}px !important`,
        boxShadow: lxoSurface.shadow,
        padding: `${lxoSpace.xxl}px ${lxoSpace.xl}px`,
        marginBottom: `${lxoSpace.lg}px`,
      }}
      className="story-mood-card"
    >
      <div className="storyMoodTitle">Today&apos;s Team Mood</div>
      <div className="storiesWrapper">
        <div className="storiesRow">
          {visibleStories.map((story, index) => (
            <div
              key={story.id}
              className="storyItem"
              onClick={() => handlerClickStory(index)}
            >
              {story.note && <div className="tooltip">{story.note}</div>}
              {story.status && (
                <div className="storyStatus">{story.status}</div>
              )}

              <div className="storyAvatar">
                <img src={story.img} alt={story.name} />
              </div>

              <div className="storyName" title={story.name}>
                {story.name}
              </div>
            </div>
          ))}

          {overflowCount > 0 && (
            <div
              className="storyItem storyItem--overflow"
              role="button"
              tabIndex={0}
              onClick={() => setExpanded(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setExpanded(true)
                }
              }}
            >
              <div className="storyAvatar storyAvatar--overflow">
                <span className="storyOverflowCount">+{overflowCount}</span>
              </div>
              <div className="storyName storyName--muted">more</div>
            </div>
          )}
        </div>
      </div>

      <ModalStoryShare {...propsModalStoryShare} />
    </Card>
  )
}
